import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MainStackProps } from '../../../../types/screens';
import {
  NativeSyntheticEvent,
  StyleSheet,
  Text,
  TextInput,
  TextInputSubmitEditingEventData,
  View,
} from 'react-native';
import TvPressable from '../../../../components/tv_pressable';
import { commonStyles } from '../../../../common/styles';
import KeyWrapper from '../../../../components/key_wrapper';
import AppColors from '../../../../common/colors';
import useGlobalStyles from '../../../../common/style_context';
import localization from '../../../../common/localization';
import OptionsButton from './options_button';
import { useFocusState } from '../../../../common/hooks';
import States from '../../../../common/states';
import { observer } from 'mobx-react-lite';
import Device from '../../../../common/device';
import * as Sentry from '@sentry/react-native';

type Props = MainStackProps<'DebugDialogTv'>;

const dialogStyles = [
  `w=800,bg=${AppColors.containerGray},p=24,br=16`,
  'fs=32,fw=700,c=white,tc,mb=36',
  'fs=32,fw=700,c=white,mb=8',
  `f=1,bg=${AppColors.containerGray},fs=28,fw=400,c=white`,
];

const DebugDialogTv = observer<Props>(({ navigation }) => {
  const focusState = useFocusState(4);

  const serial = useMemo(() => Device.getUuid(), []);

  const playerLog = States.settingsState.playerLog;
  const fetchLog = States.settingsState.fetchLog;
  const devServer = States.settingsState.devServer;

  const [feedback, setFeedback] = useState(false);
  const ref = useRef<TextInput>(null);

  useEffect(() => {
    console.log(
      `${States.mainState.session?.sidName}: ${States.mainState.session?.sid}`,
    );
    console.log(`Media token: ${States.mainState.mediaToken}`);
    console.log(`Serial: ${serial}`);
  }, []);

  useEffect(() => {
    if (feedback) setTimeout(() => ref.current?.focus());
  }, [feedback]);

  const [container, titleStyle, textStyle, inputStyle] =
    useGlobalStyles(dialogStyles);

  const up = useCallback((keyUp?: boolean) => {
    if (keyUp) return;
    focusState.decrement();
  }, []);
  const down = useCallback((keyUp?: boolean) => {
    if (keyUp) return;
    focusState.increment();
  }, []);
  const confirm = useCallback(() => {
    const f = focusState._focused;
    switch (f) {
      case 0:
        States.settingsState.playerLog = !States.settingsState.playerLog;
        break;
      case 1:
        States.settingsState.fetchLog = !States.settingsState.fetchLog;
        break;
      case 2:
        States.settingsState.devServer = !States.settingsState.devServer;
        break;
      case 3:
        setFeedback(true);
        break;
    }
  }, []);
  const cancel = useCallback(() => {
    if (feedback) setFeedback(false);
    else navigation.pop();
  }, [feedback]);

  const onSubmit = useCallback(
    (e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => {
      const message = e.nativeEvent.text;
      Sentry.captureException(new UserReport(message), {
        user: { username: States.mainState._account?.login },
        level: 'info',
      });
    },
    [],
  );
  const blur = useCallback(() => setFeedback(false), []);

  return (
    <View style={styles.container}>
      <TvPressable onPress={cancel} style={commonStyles.fill} />
      <KeyWrapper
        up={up}
        down={down}
        confirm={confirm}
        cancel={cancel}
        style={container}>
        <Text style={titleStyle}>{localization.settings.aboutDebug}</Text>
        <Text style={textStyle}>
          {States.mainState.session?.sidName}: {States.mainState.session?.sid}
        </Text>
        <Text style={textStyle}>
          Media token: {States.mainState.mediaToken}
        </Text>
        <Text style={textStyle}>
          Stream standart: {States.mainState._settings?.stream_standard.value}
        </Text>
        <Text style={textStyle}>Serial: {serial}</Text>
        <OptionsButton
          text={localization.settings.aboutPlayerLog}
          value={playerLog}
          index={0}
          focusState={focusState}
          onPress={confirm}
        />
        <OptionsButton
          text={localization.settings.aboutFetchLog}
          value={fetchLog}
          index={1}
          focusState={focusState}
          onPress={confirm}
        />
        <OptionsButton
          text={localization.settings.aboutDevServer}
          value={devServer}
          index={2}
          focusState={focusState}
          onPress={confirm}
        />
        <OptionsButton
          text={localization.settings.aboutSendReport}
          index={3}
          focusState={focusState}
          onPress={confirm}
        />
        {feedback && (
          <View style={commonStyles.fill}>
            <TextInput
              placeholder={localization.general.message}
              placeholderTextColor={'white'}
              onSubmitEditing={onSubmit}
              onBlur={blur}
              style={inputStyle}
              ref={ref}
            />
          </View>
        )}
      </KeyWrapper>
    </View>
  );
});
DebugDialogTv.displayName = 'DebugDialogTv';

class UserReport extends Error {}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default DebugDialogTv;
